import Service, { APIVersion } from 'additiv-services';
import { InvestmentApplicationInfo } from 'core/types/api/Api';
import { getCustomServiceLayerHeader } from 'additiv-services/setup';
import {
    CUSTOM_SERVICE_LAYER_SERVICES,
    getCustomServiceLayerBaseUrl,
} from '../constants';
import { getQueryStringByObject } from '../../additiv-services/utils/queryUtils';

interface CalculateModelPortfolioAllocationsPayload {
    modelPortfolioId: number;
    data: [
        {
            securityId: number;
            allocation: number;
        },
    ];
}

interface PortfolioAllocationsResponseData {}

interface ResponseData<T> {
    data: T;
}

class CustomInvestmentService extends Service {
    static #baseServiceUrl = getCustomServiceLayerBaseUrl(
        CUSTOM_SERVICE_LAYER_SERVICES.INVESTMENT,
    );

    static #baseServiceUrlV11 = getCustomServiceLayerBaseUrl(
        CUSTOM_SERVICE_LAYER_SERVICES.INVESTMENT,
        APIVersion.V11,
    );

    static #baseServiceUrlv10Contacts = getCustomServiceLayerBaseUrl(
        CUSTOM_SERVICE_LAYER_SERVICES.CONTACTS,
        APIVersion.V10,
    );

    static calculateModelPortfolioAllocations({
        modelPortfolioId,
        data,
    }: CalculateModelPortfolioAllocationsPayload):
        | Promise<ResponseData<PortfolioAllocationsResponseData>>
        | [
              Promise<ResponseData<PortfolioAllocationsResponseData>>,
              () => void,
          ] {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${modelPortfolioId}/allocations`,
            data,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static checkIsFzAndFzpEnabled(contactId) {
        return Service.getRequest(`${this.#baseServiceUrl}/${contactId}/fz`, {
            isSecure: true,
            axiosSetup: { headers: getCustomServiceLayerHeader() },
        });
    }

    static getInvestmentAppsByContainerId({
        containerId,
        language,
    }):
        | Promise<ResponseData<InvestmentApplicationInfo[]>>
        | [Promise<ResponseData<InvestmentApplicationInfo[]>>, () => void] {
        const queryString = getQueryStringByObject({ language });

        return Service.getRequest(
            `${
                this.#baseServiceUrl
            }/${containerId}/container-application-info?${queryString}`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getSubstituteExclusionOptions({ preferenceKey, language, ...rest }) {
        const queryString = getQueryStringByObject({ ...rest, language });

        return Service.getRequest(
            `${
                this.#baseServiceUrl
            }/${preferenceKey}/getSubstituteExclusion?${queryString}`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getApplicationList({
        contactId, memberId, language, ...rest
    }) {
        const queryString = getQueryStringByObject({ ...rest, language });

        return Service.getRequest(
            `${
                this.#baseServiceUrl
            }/${contactId}/${memberId}/applications?${queryString}`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static updateFees({ investmentApplicationId, payload }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}/update-fees`,
            payload,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static deleteFee({ investmentApplicationId, feeType }) {
        return Service.deleteRequest(
            `${
                this.#baseServiceUrl
            }/${investmentApplicationId}/fees?feeType=${feeType}`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getData(params) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/projection?data=${params}`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static postData(data) {
        return Service.postRequest(
            `${this.#baseServiceUrlV11}/projection`,
            data,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static cancelApplication({ applicationId }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${applicationId}/cancel`,
            null,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static cancelApplicationsByContainerId({ containerId }) {
        return Service.deleteRequest(
            `${this.#baseServiceUrl}/${containerId}/cancel-application`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static createNewInvestment({ contactId, language, data }) {
        const queryString = getQueryStringByObject({ contactId, language });

        return Service.postRequest(
            `${this.#baseServiceUrl}/complete?${queryString}`,
            data,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getInvestmentApplications({
        memberId,
        pageNumber,
        language,
        pageSize,
    }) {
        const queryString = getQueryStringByObject({
            pageNumber,
            language,
            pageSize,
        });

        return Service.getRequest(
            `${this.#baseServiceUrl}/${memberId}/applications?${queryString}`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static reGenerateInvestmentApplicationDocuments({
        investmentApplicationId,
    }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}/regenerate`,
            null,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getGeneralAgencies() {
        return Service.getRequest(`${this.#baseServiceUrl}/GeneralAgencies`, {
            isSecure: true,
            axiosSetup: { headers: getCustomServiceLayerHeader() },
        });
    }

    static getDawReport({ from, to }) {
        return Service.getRequest(
            `${
                this.#baseServiceUrl
            }/eSign-report?dateFrom=${from}&dateTo=${to}`,
            {
                isSecure: true,
                axiosSetup: {
                    headers: getCustomServiceLayerHeader(),
                    responseType: 'blob',
                },
            },
        );
    }

    static getLastApplicationByPortfolio({ portfolioId, language }) {
        const queryString = getQueryStringByObject({
            language,
        });

        return Service.getRequest(
            `${
                this.#baseServiceUrl
            }/${portfolioId}/last-application-by-portfolio?${queryString}`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getClientInvestmentApplications({
        pageNumber,
        language,
        pageSize,
        clientId,
        memberId,
    }) {
        const queryString = getQueryStringByObject({
            pageNumber,
            language,
            pageSize,
        });

        return Service.getRequest(
            `${
                this.#baseServiceUrl
            }/${memberId}/applications/${clientId}?${queryString}`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getInvestmentApplicationOverview({
        language,
        containerOrApplicationId,
        isContainer = false,
    }) {
        const queryString = getQueryStringByObject({ language });

        return Service.getRequest(
            `${this.#baseServiceUrl}/${containerOrApplicationId}/${
                isContainer ? 'container-application-info' : 'application-info'
            }?${queryString}`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static cancelInvestmentApplications({ investmentApplicationId }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}/cancel`,
            {},
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static saveProductDetails(payload) {
        return Service.postRequest(`${this.#baseServiceUrl}/save`, payload, {
            isSecure: true,
            axiosSetup: { headers: getCustomServiceLayerHeader() },
        });
    }

    static savePersonalDetails({ investmentApplicationId, ...rest }) {
        return Service.patchRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}/personal`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static saveStrategyData({ investmentApplicationId, ...rest }) {
        return Service.patchRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}/strategy`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static saveKYCDone({ investmentApplicationId, ...rest }) {
        return Service.patchRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}/kyc-done`,
            rest?.kycDone,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static saveKYCDoneByContainer({ id, ...rest }) {
        return Service.putRequest(
            `${this.#baseServiceUrl}/container/${id}/kyc-done`,
            rest?.kycDone,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static saveIdUploadDone({ investmentApplicationId, ...rest }) {
        return Service.patchRequest(
            `${
                this.#baseServiceUrl
            }/${investmentApplicationId}/set-id-provided`,
            rest?.idProvided,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static saveProductData({ investmentApplicationId, ...rest }) {
        return Service.patchRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}/product`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static saveAgentData({ investmentApplicationId, ...rest }) {
        return Service.patchRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}/agent`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static saveCustodyFee({ investmentApplicationId, ...rest }) {
        return Service.patchRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}/fees`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static saveWithdrawalData({ investmentApplicationId, ...rest }) {
        return Service.patchRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}/withdrawal`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static calculateWithdrawalPlanData({
        investmentApplicationId,
        language,
        ...rest
    }) {
        const queryString = getQueryStringByObject({ ...rest, language });

        return Service.getRequest(
            `${
                this.#baseServiceUrl
            }/${investmentApplicationId}/withdrawalPlan?${queryString}`,
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static finalizeNewPortfolio({ investmentApplicationId, ...rest }) {
        return Service.patchRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}/finalize`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static finalizeNewPortfoliosByContainerId({ containerId, ...rest }) {
        return Service.putRequest(
            `${this.#baseServiceUrl}/container/${containerId}/finalize`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static renderGeneralDocuments({ containerId }) {
        return Service.postRequest(
            `${
                this.#baseServiceUrl
            }/container/${containerId}/render-general-documents`,
            {},
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static generateDocuments({ investmentApplicationId }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}/documents`,
            {},
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static generateDocumentsNew({ investmentApplicationId, language }) {
        return Service.postRequest(
            `${
                this.#baseServiceUrl
            }/${investmentApplicationId}/documents/generate`,
            {},
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static generateDocumentsWithNewAPI({ contactId, investmentApplicationId, language }) {
        return Service.postRequest(
            `${
                this.#baseServiceUrlv10Contacts
            }/${contactId}/investments/${investmentApplicationId}/documents/generate`,
            {},
            {
                isSecure: true,
                axiosSetup: { headers: { 'accept-language': language } },
            },
        );
    }

    static getQuestionnaireAnswers({ id }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${id}/questionnaire`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getQuestionnaireAnswersByContainer({ id }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/container/${id}/questionnaire`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static postQuestionnaireAnswers({ data, id }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${id}/questionnaire`,
            data,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static postQuestionnaireAnswersByContainer({ data, id }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/container/${id}/questionnaire`,
            data,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static signDocuments({ investmentApplicationId, isNewFlow = false }) {
        return Service.postRequest(
            `${
                this.#baseServiceUrl
            }/multi-doc?investmentApplicationId=${investmentApplicationId}&isNewFlow=${isNewFlow}`,
            {},
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static changePlanApplication({ portfolioId, ...rest }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${portfolioId}/changePlanApplication`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static changePlanApplicationNew({ contactId, portfolioId, ...rest }) {
        return Service.postRequest(
            `${this.#baseServiceUrlv10Contacts}/${contactId}/portfolios/${portfolioId}/change-plan`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static changePlan({ investmentApplicationId, isNewFlow = false }) {
        return Service.postRequest(
            `${
                this.#baseServiceUrl
            }/${investmentApplicationId}/changePlan?isNewFlow=${isNewFlow}`,
            {},
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getInvestmentApplication({ investmentApplicationId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getKudi({ portfolioId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/kudi/${portfolioId}`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getCaseManagementDetails({ portfolioId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${portfolioId}/caseManagementDetails`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static makeIbanReservation({ investmentApplicationId }) {
        const queryString = getQueryStringByObject({ investmentApplicationId });

        return Service.postRequest(
            `${this.#baseServiceUrl}/makeIbanReservation?${queryString}`,
            {},
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getKudiSaldo({ portfolioId }) {
        return Service.getRequest(
            `${this.#baseServiceUrl}/${portfolioId}/kudiSaldo`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getInvestmentDataReport({ from, to }) {
        return Service.getRequest(
            `${
                this.#baseServiceUrl
            }/get-investment-data-report?startDate=${from}&endDate=${to}`,
            {
                isSecure: true,
                axiosSetup: {
                    headers: getCustomServiceLayerHeader(),
                    responseType: 'blob',
                },
            },
        );
    }

    static generateContainerId({ investmentApplicationId }) {
        return Service.patchRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}/add-container`,
            null,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    // rest -> {advisoryDocumentId, containerId, modelPortfolioId}
    static postChangeExisting3bStrategy({ portfolioId, ...rest }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${portfolioId}/change-strategy`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    // rest -> {advisoryDocumentId, containerId, modelPortfolioId}
    static putChangeExisting3bStrategy({ applicationId, ...rest }) {
        return Service.putRequest(
            `${this.#baseServiceUrl}/${applicationId}/change-strategy`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    // rest -> {advisoryDocumentId, containerId, isSell, amount}
    static postChangeExisting3bInvestmentAmount({ portfolioId, ...rest }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${portfolioId}/change-amount`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    // rest -> {advisoryDocumentId, containerId, isSell, amount}
    static putChangeExisting3bInvestmentAmount({ applicationId, ...rest }) {
        return Service.putRequest(
            `${this.#baseServiceUrl}/${applicationId}/change-amount`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static async deleteInvestmentApplication({ investmentApplicationId }) {
        return Service.deleteRequest(
            `${this.#baseServiceUrl}/${investmentApplicationId}`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static changeWidthdrawalPlan({ portfolioId, ...rest }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${portfolioId}/withdrawal-plan`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static stopWithdrawalPlan({ portfolioId }) {
        return Service.deleteRequest(
            `${this.#baseServiceUrl}/${portfolioId}/withdrawal-plan`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static createFakeInvestorProfileCheckingApplication({
        portfolioId,
        ...rest
    }) {
        return Service.postRequest(
            `${this.#baseServiceUrl}/${portfolioId}/followup-investor-profile`,
            rest,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static cancelFakeInvestorProfileCheckingApplication({ containerId }) {
        return Service.deleteRequest(
            `${this.#baseServiceUrl}/${containerId}/followup-investor-profile`,
            {
                isSecure: true,
                axiosSetup: { headers: getCustomServiceLayerHeader() },
            },
        );
    }

    static getFollowUpInvestorProfileReport({ from, to }) {
        return Service.getRequest(
            `${
                this.#baseServiceUrl
            }/report/followup-investor-profile?StartDate=${from}&EndDate=${to}`,
            {
                isSecure: true,
                axiosSetup: {
                    headers: getCustomServiceLayerHeader(),
                    responseType: 'blob',
                },
            },
        );
    }

    static postRecommendedStrategyDeviatedApplicationsReport({ from, to }) {
        return Service.postRequest(
            `${
                this.#baseServiceUrl
            }/report/recommended-strategy-deviation`,
            {
                startDate: from,
                endDate: to,
            },
            {
                isSecure: true,
                axiosSetup: {
                    headers: getCustomServiceLayerHeader(),
                    responseType: 'blob',
                },
            },
        );
    }
}

export default CustomInvestmentService;
